import create from 'zustand'
import { combine } from 'zustand/middleware'

export type FindingSeverity = 'error' | 'warning' | 'info'

/** A finding of a check the AI chat shows in the scene (`show_findings`). */
export interface SceneFinding {
  rule: string
  severity: FindingSeverity
  title: string
  detail: string
  elementGuids: string[]
}

/** The "!" whose findings are open: its spot and the elements it outlines meanwhile. */
export interface OpenFindingMarker {
  /** `markerGroups` key: the marker's spot. */
  key: string
  elementGuids: string[]
  severity: FindingSeverity
}

interface AIChatFindingsState {
  // The findings the AI chat shows in the scene per project, until the user
  // removes them (the chat's chip, or one by one at each "!") or the agent
  // shows others.
  findingsByProject: Record<string, SceneFinding[]>
  // Projects whose findings the user hid (the chip's eye) without removing
  // them; new findings show again.
  hiddenByProject: Record<string, boolean>
  // The "!" whose findings are open in the scene per project: its elements
  // are outlined while it is open. One at a time per project.
  openMarkerByProject: Record<string, OpenFindingMarker | null>
}

/** Whether two findings are the same (the agent may pass one twice). */
export const sameFinding = (a: SceneFinding, b: SceneFinding): boolean =>
  a.rule === b.rule && a.severity === b.severity && a.title === b.title && a.detail === b.detail

const initialState: AIChatFindingsState = {
  findingsByProject: {},
  hiddenByProject: {},
  openMarkerByProject: {},
}

/**
 * The AI chat's check findings in the scene: written by the chat (navbar), read by the editor's
 * markers (`AIChatFindingMarkers`) and scene bridge.
 */
const useAIChatFindingsStore = create(
  combine(initialState, set => ({
    setFindings: (projectId: string, findings: SceneFinding[]) =>
      set(state => ({
        findingsByProject: { ...state.findingsByProject, [projectId]: findings },
        hiddenByProject: { ...state.hiddenByProject, [projectId]: false },
        openMarkerByProject: { ...state.openMarkerByProject, [projectId]: null },
      })),
    toggleHidden: (projectId: string) =>
      set(state => ({
        hiddenByProject: {
          ...state.hiddenByProject,
          [projectId]: !state.hiddenByProject[projectId],
        },
        openMarkerByProject: { ...state.openMarkerByProject, [projectId]: null },
      })),
    /**
     * Removes one finding from these elements (the "!" it was dismissed at), also when the agent
     * passed it twice; at other elements it stays. Sets the open "!" to what is left of it (null
     * closes it).
     */
    dismissFinding: (
      projectId: string,
      finding: SceneFinding,
      elementGuids: string[],
      openMarker: OpenFindingMarker | null,
    ) =>
      set(state => ({
        findingsByProject: {
          ...state.findingsByProject,
          [projectId]: (state.findingsByProject[projectId] ?? [])
            .map(other =>
              sameFinding(other, finding)
                ? {
                    ...other,
                    elementGuids: other.elementGuids.filter(guid => !elementGuids.includes(guid)),
                  }
                : other,
            )
            .filter(other => other.elementGuids.length > 0),
        },
        openMarkerByProject: { ...state.openMarkerByProject, [projectId]: openMarker },
      })),
    setOpenMarker: (projectId: string, marker: OpenFindingMarker | null) =>
      set(state => ({
        openMarkerByProject: { ...state.openMarkerByProject, [projectId]: marker },
      })),
  })),
)

export default useAIChatFindingsStore
